import { enqueueSnackbar } from 'notistack';
import { useReducer, useCallback } from 'react';

import { HttpState, HttpActionTypes } from '@/types/http-type';
import { axiosInstance, poster } from '@/api/api-client';
import { AxiosError } from 'axios';
import { CUSTOM_INTERCEPTOR } from '@/constants/interceptor';

const httpReducer = (httpState: HttpState, action: HttpActionTypes): HttpState => {
  switch (action.type) {
    case 'SEND':
      return { loading: true, error: null, data: null, message: null };
    case 'RESPONSE':
      return {
        ...httpState,
        loading: false,
        data: action.data,
        message: action.message || 'Success',
      };
    case 'ERROR':
      enqueueSnackbar(action.error, { variant: 'error' });
      return { loading: false, error: action.error, data: null, message: null };
    default:
      throw new Error('Invalid action type');
  }
};

const useHttp = () => {
  const [httpState, dispatchHttp] = useReducer(httpReducer, {
    loading: false,
    error: null,
    data: null,
    message: null,
  });

  const sendRequest = useCallback(async (url: string, method: string, sendData?: any) => {
    const postData = { ...sendData };
    dispatchHttp({ type: 'SEND' });

    /**
     * CASE: send request method = 'GET'
     */
    if (method === 'GET') {
      await axiosInstance
        .get(url, { params: postData })
        .then((result) => {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const data = result?.data.data;

          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          dispatchHttp({ type: 'RESPONSE', data, message: 'Success' });
        })
        .catch((error) => {
          console.log('get error >>>>>', error);

          // @ts-ignore
          if (CUSTOM_INTERCEPTOR.MESSAGE[error?.code])
            // @ts-ignore
            error.message = CUSTOM_INTERCEPTOR.MESSAGE[error?.code];

          const { message, request } = error;

          dispatchHttp({ type: 'ERROR', error: `${message} Code : ${request?.status}` });
        });
    }

    /**
     * CASE: send request method = 'POST'
     */
    if (method === 'POST') {
      try {
        let res;
        // eslint-disable-next-line prefer-const
        res = await poster(url, postData);

        const { data, message, success } = res.data;
        if (success) {
          dispatchHttp({ type: 'RESPONSE', data, message: message || 'Success' });
        } else {
          dispatchHttp({ type: 'ERROR', error: message || 'Error' });
        }
      } catch (error) {
        console.log('post error >>>>>', error);

        // @ts-ignore
        if (CUSTOM_INTERCEPTOR.MESSAGE[error?.code])
          // @ts-ignore
          error.message = CUSTOM_INTERCEPTOR.MESSAGE[error?.code];

        const { message } = error as AxiosError;

        dispatchHttp({ type: 'ERROR', error: `${message}` });
      }
    }

    /**
     * CASE: send request method = 'PUT'
     */
    if (method === 'PUT') {
      await axiosInstance
        .put(url, postData)
        .then((result) => {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const { data, success, message } = result?.data;

          // if response success return message, if error return error
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          success
            ? dispatchHttp({ type: 'RESPONSE', data, message: message || 'Success' })
            : dispatchHttp({ type: 'ERROR', error: message || 'Error' });
        })
        .catch((error) => {
          console.log('put error >>>>>', error);

          // @ts-ignore
          if (CUSTOM_INTERCEPTOR.MESSAGE[error?.code])
            // @ts-ignore
            error.message = CUSTOM_INTERCEPTOR.MESSAGE[error?.code];

          const { message, request } = error;

          dispatchHttp({ type: 'ERROR', error: `${message} Code : ${request?.status}` });
        });
    }
  }, []);

  return {
    loading: httpState?.loading,
    data: httpState?.data,
    error: httpState?.error,
    message: httpState?.message,
    sendRequest,
  };
};

export default useHttp;
